import {
    GroupRideRiderContract,
    GroupRideRiderLocationUpdateContract,
    GroupRideRiderLocationUpdatePropertiesContract,
    GroupRideStatusRequestContract,
    ProfileGroupContract,
    RiderColor,
} from '@polaris-customerweb/sharedcomponents.core';
import { GeoCoordinate } from '@polaris-customerweb/sharedcomponents.shared';
import { Feature, LineString } from 'geojson';
import { cloneDeep } from 'lodash-es';
import { GeoJsonLineString } from './geojson-linestring';
import { MapPoint } from './map-point';
import { RideGroupAlert } from './ride-group-alert';
import { RideGroupRider } from './ride-group-rider';

export class RideGroupRiderStatus implements MapPoint {
    static maxLeaderLengthInMilliseconds = 3600000;
    contract: GroupRideRiderLocationUpdateContract;
    private _leaderLine?: number[][];
    private _leaderLineGeoJson?: Feature<LineString>;
    private _time: Date;
    private inactiveRiderPeriod?: number;

    constructor(
        contract: GroupRideRiderLocationUpdateContract = {
            geometry: {
                coordinates: [],
                type: 'MultiPoint',
            },
            properties: {},
            type: 'Feature',
        },
        inactiveRiderPeriod?: number
    ) {
        this.inactiveRiderPeriod = inactiveRiderPeriod;
        this.contract = cloneDeep(contract);
    }

    get color(): RiderColor {
        return this.profile.color ?? 'Blue';
    }

    get deviceId(): string {
        return this.properties.deviceId ?? '';
    }

    get geoCoordinate(): GeoCoordinate {
        const coordinateArray = this.contract.geometry.coordinates;

        return GeoCoordinate.fromCoordinateArray(
            coordinateArray[coordinateArray.length - 1]
        );
    }

    get geoCoordinateAsLeaderLine(): number[][] {
        return [
            [this.geoCoordinate.lng, this.geoCoordinate.lat, 0, Date.now()],
        ];
    }

    get groups(): ProfileGroupContract[] {
        return this.properties.groups ?? [];
    }

    get email(): string {
        return this.profile.email ?? '';
    }

    get handle(): string {
        return this.profile.handle ?? '';
    }

    get heading(): number {
        return this.properties.heading ?? 0;
    }

    get id(): string {
        return this.properties.id ?? '';
    }

    get leaderLine(): number[][] | undefined {
        const coordinateArrays = this.contract.geometry.coordinates;

        if (
            !this._leaderLine &&
            coordinateArrays &&
            coordinateArrays.length > 1
        ) {
            this._leaderLine = coordinateArrays.map((coordinate) => [
                ...coordinate,
            ]);
        }

        return this._leaderLine;
    }
    set leaderLine(leaderLine: number[][]) {
        this.contract.geometry.coordinates = [...leaderLine];
        this._leaderLine = undefined;
        this._leaderLineGeoJson = undefined;
    }

    get leaderLineGeoJson(): Feature<LineString> | undefined {
        if (!this._leaderLineGeoJson && this.leaderLine) {
            this._leaderLineGeoJson = new GeoJsonLineString(
                this.leaderLine.map((i) => [i[0], i[1]]),
                {
                    name: this.handle,
                    profileColor: this.color,
                }
            );
        }

        return this._leaderLineGeoJson;
    }

    get profile(): GroupRideRiderContract {
        if (!this.properties.profile) {
            this.properties.profile = {};
        }

        return this.properties.profile;
    }

    get properties(): GroupRideRiderLocationUpdatePropertiesContract {
        if (!this.contract.properties) {
            this.contract.properties = {};
        }

        return this.contract.properties;
    }

    get riderId(): string {
        return this.properties.riderId ?? '';
    }

    get isInAlert(): boolean {
        return this.properties.faults?.includes('helpAlert') ?? false;
    }

    get speedKph(): number {
        return this.properties.speedKmh ?? 0;
    }

    get statusInactive(): boolean {
        if (this.properties.deletionDate) {
            return true;
        } else {
            return false;
        }
    }

    get vehicleType(): string {
        return this.properties.vehicleType ?? '';
    }

    get vehicleIconType(): string {
        const type = this.properties.vehicleType;
        if (type === 'ATV') {
            return 'ATV';
        } else if (type === 'IND' || type === 'VIC') {
            return 'Bike';
        } else if (type === 'SLG') {
            return 'Slingshot';
        } else if (type === 'SNO') {
            return 'SLED';
        } else {
            return 'ORV';
        }
    }

    get time(): Date {
        if (!this._time) {
            const timestamp = this.properties.time;

            if (timestamp) {
                this._time = new Date(timestamp);
            }
        }

        return this._time;
    }

    set time(time: Date) {
        this._time = time;
        this.properties.time = time.getTime();
    }

    isInGroup(groupId: string): boolean {
        return (
            this.groups.find((group) => group.groupId === groupId) !== undefined
        );
    }

    appendToLeaderLine(line: number[][]) {
        const dropOffTime =
            Date.now() - RideGroupRiderStatus.maxLeaderLengthInMilliseconds;
        const leaderLine = this.leaderLine || [];
        const filteredCoordinates = leaderLine.filter(
            (x) => x[3] > dropOffTime
        );
        this.leaderLine = [...filteredCoordinates, ...line];
    }

    clone(): RideGroupRiderStatus {
        return new RideGroupRiderStatus(this.contract);
    }

    toContract(): GroupRideRiderLocationUpdateContract {
        return cloneDeep(this.contract);
    }

    toGeoJson(): GeoJSON.Feature<GeoJSON.Point> {
        const geoJson: GeoJSON.Feature<GeoJSON.Point> = {
            geometry: {
                coordinates: [
                    this.geoCoordinate?.longitude,
                    this.geoCoordinate?.latitude,
                ],
                type: 'Point' as const,
            },
            properties: {
                bearing: this.heading,
                connectedGroupride: true,
                deviceId: this.deviceId,
                helpAlert: this.isInAlert ? 'helpAlert' : undefined,
                id: this.id,
                name: this.handle,
                profileColor: this.isNotUpdating() ? 'Gray' : this.color,
            },
            type: 'Feature' as const,
        };
        if (this.vehicleType) {
            geoJson.properties ??= {};
            geoJson.properties.type = this.vehicleIconType;
        }

        return geoJson;
    }

    isNotUpdating(): boolean {
        const currentDate = Date.now();
        const difference = currentDate - this.time.getTime();

        return this.inactiveRiderPeriod
            ? difference > this.inactiveRiderPeriod
            : false;
    }

    toRideGroupRider(): RideGroupRider {
        return new RideGroupRider({
            color: this.color,
            coordinates: undefined,
            deviceId: this.deviceId,
            email: undefined,
            handle: this.handle,
            id: this.id,
            riderId: this.riderId,
            status: undefined,
        });
    }

    toRideGroupStatusRequest(): GroupRideStatusRequestContract {
        const request: GroupRideStatusRequestContract = {
            riderDeviceToFollow: this.deviceId,
            riderToFollow: this.riderId,
            riderToFollowLastUpdated:
                this.contract.geometry.coordinates.length > 1 &&
                this.properties.time
                    ? this.properties.time
                    : new Date().getTime() -
                      RideGroupRiderStatus.maxLeaderLengthInMilliseconds,
        };
        return request;
    }

    toRideGroupAlert(): RideGroupAlert {
        return {
            riderFeatureId: this.id,
            riderId: this.riderId,
        } as RideGroupAlert;
    }
}
